<template>
  <div class="app-container">
    <!-- 顶部筛选 -->
    <el-card shadow="never" class="sh-toolbar-card">
      <div class="sh-toolbar">
        <el-select v-model="statusFilter" class="sh-filter-status" placeholder="筛选状态" clearable @change="onFilterChange">
          <el-option v-for="(label, k) in SessionStatusMap" :key="k" :label="label" :value="Number(k)" />
        </el-select>
        <el-select v-model="agentFilter" class="sh-filter-agent" placeholder="筛选客服" clearable filterable @change="onFilterChange">
          <el-option v-for="a in agents" :key="a.id" :label="a.nickname" :value="a.id" />
        </el-select>
        <el-select v-model="closeCategoryFilter" class="sh-filter-close" placeholder="结束分类" clearable @change="onFilterChange">
          <el-option v-for="(meta, k) in CloseCategoryMap" :key="k" :label="meta.label" :value="k" />
        </el-select>
        <el-input
          v-model="keyword"
          class="sh-filter-keyword"
          placeholder="搜索客户名/会话ID"
          clearable
          @keyup.enter.native="searchSessions"
          @clear="searchSessions"
        />
        <div class="sh-toolbar-spacer" />
        <el-button :loading="exporting" @click="handleExport"><i class="el-icon-download"></i> 导出 Excel</el-button>
      </div>
    </el-card>

    <!-- 会话列表 -->
    <el-table :data="sessions" v-loading="loading" border style="width:100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="customerName" label="客户" width="120" show-overflow-tooltip />
      <el-table-column prop="channel" label="频道" width="80" />
      <el-table-column label="来源" width="90" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.clientSource" :type="tagTypeOf(clientSourceMeta(scope.row.clientSource).color)" size="small">
            {{ clientSourceMeta(scope.row.clientSource).label }}
          </el-tag>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="IP / 地区" width="170">
        <template slot-scope="scope">
          <template v-if="scope.row.clientIp">
            <div>{{ scope.row.clientIp }}</div>
            <div v-if="scope.row.clientGeo" class="sh-geo">{{ scope.row.clientGeo }}</div>
          </template>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="statusTypeOf(scope.row.status)" size="small">{{ SessionStatusMap[scope.row.status] || '未知' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="结束分类" width="130">
        <template slot-scope="scope">
          <el-select
            v-if="scope.row.status === 3 || scope.row.status === 4"
            size="small"
            class="sh-close-select"
            :value="scope.row.closeCategory"
            placeholder="—"
            @change="(val) => handleCloseCategoryChange(scope.row, val)"
          >
            <el-option v-for="(meta, k) in CloseCategoryMap" :key="k" :label="meta.label" :value="k" />
          </el-select>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column prop="agentNickname" label="客服" width="100" show-overflow-tooltip />
      <el-table-column label="评分" width="170">
        <template slot-scope="scope">
          <template v-if="scope.row.ratingScore">
            <el-rate :value="scope.row.ratingScore" size="small" disabled />
            <div v-if="scope.row.ratingComment" class="sh-rating-comment">{{ scope.row.ratingComment }}</div>
          </template>
          <span v-else class="sh-empty">—</span>
        </template>
      </el-table-column>
      <el-table-column label="建立时间" width="160">
        <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="100">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openMessages(scope.row)">查看对话</el-button>
        </template>
      </el-table-column>
      <template slot="empty">暂无会话</template>
    </el-table>
    <el-pagination
      class="sh-pagination"
      layout="total, sizes, prev, pager, next"
      :current-page="page"
      :page-size="pageSize"
      :page-sizes="[10, 20, 50, 100]"
      :total="total"
      @current-change="onPageChange"
      @size-change="onSizeChange"
    />

    <!-- 对话记录弹窗 -->
    <el-dialog title="对话记录" :visible.sync="drawerOpen" width="560px" @closed="onDrawerClosed">
      <div v-loading="drawerLoading">
        <div v-if="drawerSession && drawerSession.clientIp" class="sh-device-desc">
          <div class="sh-device-title">客户信息</div>
          <el-row>
            <el-col :span="12" class="sh-device-cell"><span class="tk-detail-label">IP</span> {{ drawerSession.clientIp }}<span v-if="drawerSession.clientGeo" class="sh-geo sh-geo-inline">{{ drawerSession.clientGeo }}</span></el-col>
            <el-col :span="12" class="sh-device-cell">
              <span class="tk-detail-label">来源</span>
              <el-tag :type="tagTypeOf(clientSourceMeta(drawerSession.clientSource || '').color)" size="small">{{ clientSourceMeta(drawerSession.clientSource || '').label }}</el-tag>
            </el-col>
            <el-col v-if="device.os" :span="12" class="sh-device-cell"><span class="tk-detail-label">操作系统</span> {{ device.os }}</el-col>
            <el-col v-if="device.browser" :span="12" class="sh-device-cell"><span class="tk-detail-label">浏览器</span> {{ device.browser }}</el-col>
            <el-col v-if="device.resolution" :span="12" class="sh-device-cell"><span class="tk-detail-label">分辨率</span> {{ device.resolution }}</el-col>
            <el-col v-if="device.language" :span="12" class="sh-device-cell"><span class="tk-detail-label">语言</span> {{ device.language }}</el-col>
            <el-col v-if="device.timezone" :span="12" class="sh-device-cell"><span class="tk-detail-label">时区</span> {{ device.timezone }}</el-col>
          </el-row>
        </div>

        <div class="sh-msg-list">
          <div v-for="msg in drawerMessages" :key="msg.id" class="sh-msg-item">
            <div class="sh-msg-head">
              <el-tag size="small">{{ SenderTypeMap[msg.senderType] || '未知' }}</el-tag>
              <span class="sh-msg-name">{{ msg.senderName }}</span>
              <span class="sh-msg-time">{{ formatTime(msg.createTime, 'HH:mm:ss') }}</span>
            </div>
            <div class="sh-msg-content">{{ msg.content }}</div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="drawerOpen = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listAdminSessions, getSessionMessages, exportSessions, updateCloseCategory } from '@/api/customerService/session'
import { listAgents } from '@/api/customerService/admin'

const SessionStatusMap = { 0: '等待中', 1: 'AI服务中', 2: '人工服务中', 3: '已关闭', 4: '已超时' }
const SenderTypeMap = { 0: '客户', 1: 'AI', 2: '客服', 3: '系统' }
const ClientSourceMap = {
  H5: { label: 'H5', color: 'orange' },
  APP: { label: 'APP', color: 'green' },
  PCWEB: { label: 'PC Web', color: 'blue' },
  USER_CENTER: { label: '用户中心', color: 'purple' },
  OFFICIAL_SITE: { label: '官网', color: 'cyan' },
  OTHER: { label: '其他', color: 'default' }
}
const CloseCategoryMap = {
  no_conversation: { label: '无对话', color: 'default' },
  valid_conversation: { label: '有效对话', color: 'green' }
}

function tagTypeOf(color) {
  switch (color) {
    case 'green': return 'success'
    case 'orange': return 'warning'
    case 'red': return 'danger'
    case 'blue':
    case 'cyan':
    case 'geekblue': return 'primary'
    case 'purple':
    case 'default':
    default: return 'info'
  }
}

export default {
  name: 'CustomerServiceSessionHistoryList',
  data() {
    return {
      SessionStatusMap,
      SenderTypeMap,
      ClientSourceMap,
      CloseCategoryMap,
      sessions: [],
      total: 0,
      page: 1,
      pageSize: 10,
      statusFilter: undefined,
      agentFilter: undefined,
      closeCategoryFilter: undefined,
      keyword: '',
      loading: false,
      agents: [],
      drawerOpen: false,
      drawerSession: null,
      drawerMessages: [],
      drawerLoading: false,
      exporting: false
    }
  },
  computed: {
    device() {
      if (!this.drawerSession || !this.drawerSession.deviceInfo) return {}
      try {
        return JSON.parse(this.drawerSession.deviceInfo) || {}
      } catch (e) {
        return {}
      }
    }
  },
  created() {
    listAgents({ pageSize: 200 }).then(res => {
      if (res.code === 0) this.agents = res.result.list || []
    }).catch(() => {})
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      listAdminSessions({
        status: this.statusFilter,
        agentId: this.agentFilter,
        keyword: this.keyword,
        closeCategory: this.closeCategoryFilter,
        pageNum: this.page,
        pageSize: this.pageSize
      }).then(res => {
        if (res.code === 0) {
          this.sessions = res.result.list || []
          const rawTotal = res.result && res.result.total
          this.total = Number(rawTotal) > 0 ? Number(rawTotal) : 0
        }
      }).finally(() => { this.loading = false })
    },
    onFilterChange() {
      this.page = 1
      this.loadData()
    },
    searchSessions() {
      this.page = 1
      this.loadData()
    },
    onPageChange(p) {
      this.page = p
      this.loadData()
    },
    onSizeChange(ps) {
      this.page = 1
      this.pageSize = ps
      this.loadData()
    },
    clientSourceMeta(v) {
      return ClientSourceMap[v] || ClientSourceMap.OTHER
    },
    statusTypeOf(v) {
      if (v === 2) return 'success'
      if (v === 0) return 'warning'
      if (v === 3) return 'info'
      return 'primary'
    },
    tagTypeOf,
    formatTime(v, fmt) {
      if (!v) return '—'
      let s = String(v).replace('T', ' ')
      if (fmt === 'HH:mm:ss') s = s.substring(11, 19)
      else s = s.substring(0, 16)
      return s || '—'
    },
    openMessages(record) {
      this.drawerSession = record
      this.drawerOpen = true
      this.drawerLoading = true
      getSessionMessages(record.id).then(res => {
        if (res.code === 0) {
          const msgs = res.result || []
          this.drawerMessages = [].concat(msgs).reverse()
        } else {
          this.$message.error('加载消息失败')
        }
      }).catch(() => {
        this.$message.error('加载消息失败')
      }).finally(() => { this.drawerLoading = false })
    },
    onDrawerClosed() {
      this.drawerSession = null
      this.drawerMessages = []
    },
    handleCloseCategoryChange(record, val) {
      updateCloseCategory(record.id, val).then(res => {
        if (res.code === 0) {
          this.$message.success('修改成功')
          this.loadData()
        }
      }).catch(() => {
        this.$message.error('修改失败')
      })
    },
    handleExport() {
      this.exporting = true
      exportSessions({
        status: this.statusFilter,
        agentId: this.agentFilter,
        keyword: this.keyword,
        closeCategory: this.closeCategoryFilter
      }).then(res => {
        let blob = res
        if (!(res instanceof Blob)) blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = '会话历史_' + this.nowStamp() + '.xlsx'
        a.click()
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      }).catch(() => {
        this.$message.error('导出失败')
      }).finally(() => { this.exporting = false })
    },
    nowStamp() {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : '' + n)
      return d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + '_' + p(d.getHours()) + p(d.getMinutes())
    }
  }
}
</script>

<style scoped lang="scss">
.sh-toolbar-card { margin-bottom: 16px; }
.sh-toolbar-card ::v-deep  .el-card__body { padding: 14px 16px; }
.sh-toolbar { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
.sh-filter-status { width: 160px; }
.sh-filter-agent { width: 180px; }
.sh-filter-close { width: 140px; }
.sh-filter-keyword { width: 240px; }
.sh-toolbar-spacer { flex: 1; }
.sh-pagination { margin-top: 16px; text-align: right; }
.sh-geo { font-size: 11px; color: #8c8c8c; }
.sh-geo-inline { margin-left: 8px; }
.sh-close-select { width: 110px; }
.sh-rating-comment { font-size: 12px; color: #8c8c8c; margin-top: 2px; }
.sh-empty { color: #bfbfbf; }
.sh-device-title { font-weight: 600; margin-bottom: 8px; }
.sh-device-cell { margin-bottom: 6px; }
.tk-detail-label { color: #99a2b3; margin-right: 6px; }
.sh-msg-list { display: flex; flex-direction: column; margin-top: 12px; }
.sh-msg-item { padding: 10px 0; border-bottom: 1px solid #e4e7ed; }
.sh-msg-head { display: flex; align-items: center; gap: 8px; }
.sh-msg-name { font-weight: 500; }
.sh-msg-time { margin-left: auto; font-size: 12px; color: #999; }
.sh-msg-content { margin-top: 6px; white-space: pre-wrap; word-break: break-word; }
</style>